import type { MotivationData } from "@/hooks/useMotivation";

interface Props {
  data: MotivationData;
  loading: boolean;
}

function formatStudyTime(minutes: number): string {
  if (minutes < 60) return `${minutes} dk`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h} sa ${m} dk` : `${h} sa`;
}

export default function StreakLevelCard({ data, loading }: Props) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Seri */}
        <div className="flex items-center gap-4">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
            <i className="ri-fire-line text-2xl" aria-hidden="true"></i>
          </span>
          <div>
            {loading ? (
              <div className="h-8 w-16 rounded bg-background-100 animate-pulse" />
            ) : (
              <p className="text-3xl font-heading font-bold text-foreground-950">
                {data.streak}
              </p>
            )}
            <p className="text-sm text-foreground-600 mt-0.5">gün üst üste çalışma</p>
          </div>
        </div>

        {/* XP */}
        <div className="flex items-center gap-4">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
            <i className="ri-star-line text-2xl" aria-hidden="true"></i>
          </span>
          <div>
            {loading ? (
              <div className="h-8 w-16 rounded bg-background-100 animate-pulse" />
            ) : (
              <p className="text-3xl font-heading font-bold text-foreground-950">
                {data.xp.toLocaleString("tr-TR")}
              </p>
            )}
            <p className="text-sm text-foreground-600 mt-0.5">toplam XP puanı</p>
          </div>
        </div>

        {/* Toplam çalışma */}
        <div className="flex items-center gap-4">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
            <i className="ri-time-line text-2xl" aria-hidden="true"></i>
          </span>
          <div>
            {loading ? (
              <div className="h-8 w-16 rounded bg-background-100 animate-pulse" />
            ) : (
              <p className="text-3xl font-heading font-bold text-foreground-950">
                {formatStudyTime(data.totalMinutes)}
              </p>
            )}
            <p className="text-sm text-foreground-600 mt-0.5">toplam çalışma süresi</p>
          </div>
        </div>
      </div>

      {/* Seviye çubuğu */}
      <div className="mt-6 pt-5 border-t border-background-200">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground-950">
            Seviye {data.level} · {data.levelTitle}
          </p>
          <p className="text-xs text-foreground-500">
            {data.xp.toLocaleString("tr-TR")} / {data.xpToNext.toLocaleString("tr-TR")} XP
          </p>
        </div>
        <div className="mt-2 h-2.5 w-full rounded-full bg-background-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary-500 transition-all duration-500"
            style={{ width: `${loading ? 0 : data.levelProgress}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-foreground-500">
          {loading
            ? ""
            : data.levelProgress >= 100
              ? "Bir sonraki seviyeye geçtin! 🎉"
              : `Bir sonraki seviyeye ${(data.xpToNext - data.xp).toLocaleString("tr-TR")} XP kaldı`}
        </p>
      </div>
    </div>
  );
}